<template>
  <v-container fluid>
    <v-row column>
      <v-col>
        <WorkpackagesActive />
        <WorkpackagesArchived :entity="entities.workpackage" />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import entities from '@enums/entities';
import sectionStatuses from '@enums/section-status';
import toolbarTabs from '@/utils/tabs/toolbar-tabs-enum';
import { useFeature } from '@/composables/use-feature';
import { useInternationalization } from '@/composables/use-internationalization';
import { useAiStore } from '@/stores/ai';
import { useAiAgentActions } from '@/composables/use-ai-agent-actions';
import { useInputValidations } from '@/composables/input-validations';
import { useWorkpackagesStore } from '@/stores/workpackages';
import { useScenariosStore } from '@/stores/scenarios';
import { mapState, mapActions } from 'pinia';
import { get, sortBy, takeRight } from 'lodash';
import { useRouter } from 'vue-router';

/**
 * Section keys used for the AI side panel — one per home page view mode.
 */
const AI_SECTION_HOME = 'home';
const AI_SECTION_SCOPE = 'home-scope';

export default {
  setup() {
    const { aiPanelEnabled } = useFeature();
    const { t } = useInternationalization();
    const workpackagesStore = useWorkpackagesStore();
    const scenariosStore = useScenariosStore();
    const router = useRouter();
    const { isNameUnique } = useInputValidations();

    useAiAgentActions({
      actions: {
        // ─────────────────────────────────────────────────────────────────
        // Workpackage actions
        // ─────────────────────────────────────────────────────────────────

        'rename-workpackage': {
          description:
            'Rename the currently open workpackage. Do not confirm success in your message text; the system will confirm or report errors after the action completes.',
          workingLabel: 'Renaming workpackage',
          confirmationLabel: args => `rename workpackage to "${args.value}"`,
          parameters: {
            type: 'object',
            properties: {
              value: { type: 'string', description: 'The new name for the workpackage' },
            },
            required: ['value'],
          },
          handler: async ({ value: newName }) => {
            if (!newName || !workpackagesStore.selectedWorkpackage?._id) {
              return '[Tool] Action failed: no workpackage is currently open. Ask the user to open one first.';
            }
            const nameList = workpackagesStore.activeWorkpackages.map(wp => wp.name);
            const validationResult = isNameUnique(
              newName,
              workpackagesStore.selectedWorkpackage.name,
              nameList,
              t('entities.workpackage')
            );
            if (validationResult !== true) {
              return `[Tool] Action failed: ${validationResult}. Ask the user for a new name.`;
            }
            try {
              await workpackagesStore.updateWorkpackage({
                workpackageId: workpackagesStore.selectedWorkpackage._id,
                updates: { name: newName },
              });
              return `[Tool] Action succeeded: workpackage renamed to "${newName}".`;
            } catch (error) {
              console.error('AI action: error renaming workpackage:', error);
              return '[Tool] Action failed: an unexpected error occurred.';
            }
          },
        },

        'create-workpackage': {
          description:
            'Create a new workpackage with the given name. Do not confirm success in your message text; the system will confirm or report errors after the action completes.',
          workingLabel: 'Creating workpackage',
          confirmationLabel: args => `create a new workpackage named "${args.value}"`,
          parameters: {
            type: 'object',
            properties: {
              value: { type: 'string', description: 'The name for the new workpackage' },
            },
            required: ['value'],
          },
          handler: async ({ value: name }) => {
            if (name) {
              const nameList = workpackagesStore.activeWorkpackages.map(wp => wp.name);
              const validationResult = isNameUnique(
                name,
                null,
                nameList,
                t('entities.workpackage')
              );
              if (validationResult !== true) {
                return `[Tool] Action failed: ${validationResult}. Ask the user for a new name.`;
              }
            }
            try {
              const insertedWorkpackage = await workpackagesStore.addWorkpackage({});
              if (!insertedWorkpackage)
                return '[Tool] Action failed: workpackage could not be created.';
              if (name) {
                await workpackagesStore.updateWorkpackage({
                  workpackageId: insertedWorkpackage._id,
                  updates: { name },
                });
              }
              await workpackagesStore.toggleWorkpackageSetupExpanded({
                workpackage: insertedWorkpackage,
              });
              return `[Tool] Action succeeded: workpackage "${name || insertedWorkpackage.name}" created and the scope panel is now open. Inform the user they need to fill out the workpackage scope before they can start working on scenarios.`;
            } catch (error) {
              console.error('AI action: error creating workpackage:', error);
              return '[Tool] Action failed: an unexpected error occurred.';
            }
          },
        },

        'select-workpackage': {
          description:
            'Open a workpackage by name, expanding its scenario list. Use this when the user asks to "open", "select", or "switch to" a specific workpackage. Do not use when the scope editor is already open.',
          workingLabel: 'Opening workpackage',
          confirmationLabel: args => `open workpackage "${args.name}"`,
          parameters: {
            type: 'object',
            properties: {
              name: {
                type: 'string',
                description:
                  'The exact name of the workpackage to open (case-insensitive match is fine)',
              },
            },
            required: ['name'],
          },
          handler: async ({ name }) => {
            if (workpackagesStore.isWPSetupExpanded) {
              return '[Tool] Action failed: the scope editor is currently open. Ask the user to close it first.';
            }
            const wp = workpackagesStore.activeWorkpackages.find(
              w => w.name?.toLowerCase() === name?.toLowerCase()
            );
            if (!wp) {
              const names = workpackagesStore.activeWorkpackages.map(w => w.name).join(', ');
              return `[Tool] Action failed: no active workpackage named "${name}" was found. Available workpackages: ${names || 'none'}.`;
            }
            await workpackagesStore.toggleScenarioPanelExpanded({ workpackage: wp });
            return `[Tool] Action succeeded: workpackage "${wp.name}" is now open.`;
          },
        },

        'copy-workpackage': {
          description:
            'Copy (duplicate) the currently open workpackage. This is an async job — the copy will appear in the list when complete. A notification will be shown automatically.',
          workingLabel: 'Copying workpackage',
          confirmationLabel: () => 'copy the currently open workpackage',
          parameters: {
            type: 'object',
            properties: {},
            required: [],
          },
          handler: async () => {
            if (!workpackagesStore.selectedWorkpackage?._id) {
              return '[Tool] Action failed: no workpackage is currently open. Ask the user to open one first.';
            }
            try {
              await workpackagesStore.copyWorkpackage(workpackagesStore.selectedWorkpackage);
              return `[Tool] Action succeeded: a copy of "${workpackagesStore.selectedWorkpackage.name}" has been queued. A notification will appear when it finishes.`;
            } catch (error) {
              console.error('AI action: error copying workpackage:', error);
              return '[Tool] Action failed: an unexpected error occurred.';
            }
          },
        },

        'open-workpackage-scope': {
          description:
            'Open the scope editor for a workpackage by name. Use when the user wants to edit, configure, or review a workpackage\'s scope settings (dates, products, stores, assortment groups). If the user refers to "my workpackage" without a name, use the currently open workpackage.',
          workingLabel: 'Opening scope editor',
          confirmationLabel: args => `open the scope editor for "${args.name}"`,
          parameters: {
            type: 'object',
            properties: {
              name: {
                type: 'string',
                description:
                  "The name of the workpackage whose scope editor to open. Pass the currently open workpackage's name if no specific name is given.",
              },
            },
            required: ['name'],
          },
          handler: async ({ name }) => {
            const wp = workpackagesStore.activeWorkpackages.find(
              w => w.name?.toLowerCase() === name?.toLowerCase()
            );
            if (!wp) {
              const names = workpackagesStore.activeWorkpackages.map(w => w.name).join(', ');
              return `[Tool] Action failed: no active workpackage named "${name}" was found. Available workpackages: ${names || 'none'}.`;
            }
            await workpackagesStore.toggleWorkpackageSetupExpanded({ workpackage: wp });
            return `[Tool] Action succeeded: the scope editor for "${wp.name}" is now open.`;
          },
        },

        // ─────────────────────────────────────────────────────────────────
        // Scenario actions — require the scenario list panel to be open
        // ─────────────────────────────────────────────────────────────────

        'create-scenario': {
          description:
            'Create a new scenario with the given name inside the currently open workpackage. Only call this when the workpackage scenario list is open (viewMode is "scenarios").',
          workingLabel: 'Creating scenario',
          confirmationLabel: args => `create scenario "${args.name}"`,
          parameters: {
            type: 'object',
            properties: {
              name: { type: 'string', description: 'The name for the new scenario' },
            },
            required: ['name'],
          },
          handler: async ({ name }) => {
            if (
              !workpackagesStore.isScenarioPanelExpanded ||
              !workpackagesStore.selectedWorkpackage?._id
            ) {
              return '[Tool] Action failed: no workpackage scenario panel is open. Ask the user to open a workpackage first.';
            }
            const workpackageId = workpackagesStore.selectedWorkpackage._id;
            const existingNames = scenariosStore.scenarios
              .filter(s => s.workpackageId === workpackageId)
              .map(s => s.name);
            const validationResult = isNameUnique(
              name,
              null,
              existingNames,
              t('entities.scenario')
            );
            if (validationResult !== true) {
              return `[Tool] Action failed: ${validationResult}. Ask the user for a different name.`;
            }
            try {
              await scenariosStore.createScenario({
                scenario: { name: name.trim(), workpackageId },
              });
              return `[Tool] Action succeeded: scenario "${name}" created.`;
            } catch (error) {
              console.error('AI action: error creating scenario:', error);
              return '[Tool] Action failed: an unexpected error occurred.';
            }
          },
        },

        'rename-scenario': {
          description:
            'Rename the currently expanded (open) scenario row. Only call this when a scenario row is expanded and selectedScenario has an _id.',
          workingLabel: 'Renaming scenario',
          confirmationLabel: args => `rename scenario to "${args.name}"`,
          parameters: {
            type: 'object',
            properties: {
              name: { type: 'string', description: 'The new name for the scenario' },
            },
            required: ['name'],
          },
          handler: async ({ name }) => {
            // Must have both the scenario panel open AND a scenario row actually expanded
            if (
              !workpackagesStore.isScenarioPanelExpanded ||
              !scenariosStore.selectedScenario?._id
            ) {
              return '[Tool] Action failed: no scenario row is currently expanded. Ask the user to click on a scenario row first.';
            }
            const workpackageId = workpackagesStore.selectedWorkpackage?._id;
            const existingNames = scenariosStore.scenarios
              .filter(
                s =>
                  s.workpackageId === workpackageId && s._id !== scenariosStore.selectedScenario._id
              )
              .map(s => s.name);
            const validationResult = isNameUnique(
              name,
              scenariosStore.selectedScenario.name,
              existingNames,
              t('entities.scenario')
            );
            if (validationResult !== true) {
              return `[Tool] Action failed: ${validationResult}. Ask the user for a different name.`;
            }
            try {
              await scenariosStore.updateScenario({
                scenario: { id: scenariosStore.selectedScenario._id, name: name.trim() },
              });
              return `[Tool] Action succeeded: scenario renamed to "${name}".`;
            } catch (error) {
              console.error('AI action: error renaming scenario:', error);
              return '[Tool] Action failed: an unexpected error occurred.';
            }
          },
        },

        'copy-scenario': {
          description:
            'Copy (duplicate) the currently expanded scenario. This is an async job — a notification will appear when the copy is ready.',
          workingLabel: 'Copying scenario',
          confirmationLabel: () => 'copy the currently expanded scenario',
          parameters: {
            type: 'object',
            properties: {},
            required: [],
          },
          handler: async () => {
            if (
              !workpackagesStore.isScenarioPanelExpanded ||
              !scenariosStore.selectedScenario?._id
            ) {
              return '[Tool] Action failed: no scenario row is currently expanded. Ask the user to click on a scenario row first.';
            }
            try {
              await scenariosStore.copyScenario(scenariosStore.selectedScenario);
              return `[Tool] Action succeeded: a copy of "${scenariosStore.selectedScenario.name}" has been queued. A notification will appear when it finishes.`;
            } catch (error) {
              console.error('AI action: error copying scenario:', error);
              return '[Tool] Action failed: an unexpected error occurred.';
            }
          },
        },

        'delete-scenario': {
          description:
            'Permanently delete the currently expanded scenario and all its data. This cannot be undone. Always confirm with the user before calling this.',
          workingLabel: 'Deleting scenario',
          confirmationLabel: () => 'permanently delete the currently expanded scenario',
          parameters: {
            type: 'object',
            properties: {},
            required: [],
          },
          handler: async () => {
            if (
              !workpackagesStore.isScenarioPanelExpanded ||
              !scenariosStore.selectedScenario?._id
            ) {
              return '[Tool] Action failed: no scenario row is currently expanded.';
            }
            const scenarioName = scenariosStore.selectedScenario.name;
            try {
              await scenariosStore.deleteScenario(scenariosStore.selectedScenario);
              return `[Tool] Action succeeded: scenario "${scenarioName}" has been deleted.`;
            } catch (error) {
              console.error('AI action: error deleting scenario:', error);
              return '[Tool] Action failed: an unexpected error occurred.';
            }
          },
        },

        'navigate-to-section': {
          description:
            'Navigate to a workflow section (Space, Inputs, Measuring, Modelling, Assortment, Execution) for the currently expanded scenario. Only call when isScenarioPanelExpanded is true AND a scenario row is expanded (selectedScenario has _id). Never call based only on selectedScenario._id — that may be stale from a previous session.',
          workingLabel: 'Navigating to section',
          confirmationLabel: args => `navigate to ${args.section} for the current scenario`,
          parameters: {
            type: 'object',
            properties: {
              section: {
                type: 'string',
                enum: ['space', 'inputs', 'measuring', 'modelling', 'assortment', 'execution'],
                description: 'The section to navigate to',
              },
            },
            required: ['section'],
          },
          handler: async ({ section }) => {
            // Both conditions required — never trust selectedScenario alone
            if (
              !workpackagesStore.isScenarioPanelExpanded ||
              !scenariosStore.selectedScenario?._id
            ) {
              return '[Tool] Action failed: no scenario row is currently expanded on the home page. Ask the user to click on a scenario row first.';
            }
            const scenarioId = scenariosStore.selectedScenario._id;
            const tab = toolbarTabs.ALL.find(
              tabItem => tabItem.id === section || tabItem.statusSection === section
            );
            if (!tab) {
              return `[Tool] Action failed: unknown section "${section}". Valid options: space, inputs, measuring, modelling, assortment, execution.`;
            }
            const routeName =
              typeof tab.firstChild === 'function'
                ? tab.routeName // fallback to parent route — firstChild needs context we don't have here
                : tab.firstChild || tab.routeName;
            await router.push({ name: routeName, params: { scenarioId } });
            return `[Tool] Action succeeded: navigating to ${section}.`;
          },
        },

        // ─────────────────────────────────────────────────────────────────
        // Scope editor actions — require viewMode === 'scope'
        // ─────────────────────────────────────────────────────────────────

        'set-snapshot-date': {
          description:
            'Set the snapshot date for the workpackage scope. This determines which products and stores are in the selected assortment groups. Important: changing this date means assortment groups will need to be re-imported. Only call when the scope panel is open (viewMode is "scope").',
          workingLabel: 'Setting snapshot date',
          confirmationLabel: args => `set snapshot date to ${args.date}`,
          parameters: {
            type: 'object',
            properties: {
              date: { type: 'string', description: 'The snapshot date in YYYY-MM-DD format' },
            },
            required: ['date'],
          },
          handler: async ({ date }) => {
            if (!workpackagesStore.isWPSetupExpanded) {
              return '[Tool] Action failed: the scope editor is not open.';
            }
            workpackagesStore.updateWpDateSelections({ field: 'snapshotDate', value: date });
            return `[Tool] Action succeeded: snapshot date set to ${date}. Remind the user that assortment groups may need to be re-imported after changing this date. The scope has not been saved yet — ask the user to save when ready.`;
          },
        },

        'set-performance-period': {
          description:
            'Set the performance period start date and number of weeks for the workpackage scope. Only call when the scope panel is open (viewMode is "scope").',
          workingLabel: 'Setting performance period',
          confirmationLabel: args =>
            `set performance period to ${args.startDate} for ${args.weeks} weeks`,
          parameters: {
            type: 'object',
            properties: {
              startDate: { type: 'string', description: 'Start date in YYYY-MM-DD format' },
              weeks: { type: 'number', description: 'Number of weeks for the performance period' },
            },
            required: ['startDate', 'weeks'],
          },
          handler: async ({ startDate, weeks }) => {
            if (!workpackagesStore.isWPSetupExpanded) {
              return '[Tool] Action failed: the scope editor is not open.';
            }
            workpackagesStore.updateWpDateSelections({
              field: 'performanceStartDate',
              value: startDate,
            });
            workpackagesStore.updateWpDateSelections({
              field: 'performanceDateWeeks',
              value: weeks,
            });
            return `[Tool] Action succeeded: performance period set to ${startDate} for ${weeks} weeks. The scope has not been saved yet — ask the user to save when ready.`;
          },
        },

        'set-switching-period': {
          description:
            'Set the switching period start date and number of weeks for the workpackage scope. Only call when the scope panel is open (viewMode is "scope").',
          workingLabel: 'Setting switching period',
          confirmationLabel: args =>
            `set switching period to ${args.startDate} for ${args.weeks} weeks`,
          parameters: {
            type: 'object',
            properties: {
              startDate: { type: 'string', description: 'Start date in YYYY-MM-DD format' },
              weeks: { type: 'number', description: 'Number of weeks for the switching period' },
            },
            required: ['startDate', 'weeks'],
          },
          handler: async ({ startDate, weeks }) => {
            if (!workpackagesStore.isWPSetupExpanded) {
              return '[Tool] Action failed: the scope editor is not open.';
            }
            workpackagesStore.updateWpDateSelections({
              field: 'switchingStartDate',
              value: startDate,
            });
            workpackagesStore.updateWpDateSelections({ field: 'switchingDateWeeks', value: weeks });
            return `[Tool] Action succeeded: switching period set to ${startDate} for ${weeks} weeks. The scope has not been saved yet — ask the user to save when ready.`;
          },
        },

        'set-target-launch-date': {
          description:
            'Set the target launch date for the workpackage. Only call when the scope panel is open (viewMode is "scope").',
          workingLabel: 'Setting target launch date',
          confirmationLabel: args => `set target launch date to ${args.date}`,
          parameters: {
            type: 'object',
            properties: {
              date: { type: 'string', description: 'The target launch date in YYYY-MM-DD format' },
            },
            required: ['date'],
          },
          handler: async ({ date }) => {
            if (!workpackagesStore.isWPSetupExpanded) {
              return '[Tool] Action failed: the scope editor is not open.';
            }
            workpackagesStore.updateWpDateSelections({ field: 'targetLaunchDate', value: date });
            return `[Tool] Action succeeded: target launch date set to ${date}. The scope has not been saved yet — ask the user to save when ready.`;
          },
        },

        'save-scope': {
          description:
            'Save the current workpackage scope changes. Always ask the user to confirm before calling this — saving affects all scenarios in the workpackage. Only call when the scope panel is open.',
          workingLabel: 'Saving scope',
          confirmationLabel: () => 'save the current scope settings',
          parameters: {
            type: 'object',
            properties: {},
            required: [],
          },
          handler: async () => {
            if (
              !workpackagesStore.isWPSetupExpanded ||
              !workpackagesStore.selectedWorkpackage?._id
            ) {
              return '[Tool] Action failed: the scope editor is not open.';
            }
            try {
              const data = workpackagesStore.getWpDataForSave();
              await workpackagesStore.updateWorkpackage({
                workpackageId: workpackagesStore.selectedWorkpackage._id,
                updates: data,
              });
              return '[Tool] Action succeeded: workpackage scope has been saved.';
            } catch (error) {
              console.error('AI action: error saving scope:', error);
              return '[Tool] Action failed: an unexpected error occurred while saving.';
            }
          },
        },

        'run-workpackage-setup': {
          description:
            'Trigger the workpackage setup job after scope has been saved. This is a long-running async job that processes all data and makes scenarios available. Always ask the user to confirm before calling — this should only be run after all scope fields are configured and saved. Only call when the scope panel is open.',
          workingLabel: 'Running workpackage setup',
          confirmationLabel: () => 'run the workpackage setup job',
          parameters: {
            type: 'object',
            properties: {},
            required: [],
          },
          handler: async () => {
            if (!workpackagesStore.isWPSetupExpanded) {
              return '[Tool] Action failed: the scope editor is not open.';
            }
            try {
              await workpackagesStore.runWorkpackageSetupJob();
              return '[Tool] Action succeeded: workpackage setup job has been started. A notification will appear when it completes.';
            } catch (error) {
              console.error('AI action: error running workpackage setup:', error);
              return '[Tool] Action failed: an unexpected error occurred.';
            }
          },
        },
      },
    });

    return {
      aiPanelEnabled,
      t,
    };
  },

  data() {
    return {
      entities,
    };
  },

  computed: {
    ...mapState(useWorkpackagesStore, [
      'selectedWorkpackage',
      'activeWorkpackages',
      'archivedWorkpackages',
      'isScenarioPanelExpanded',
      'isWPSetupExpanded',
      'wpDateSelections',
    ]),
    ...mapState(useScenariosStore, ['scenarios', 'selectedScenario', 'scenarioStatus']),
    ...mapState(useAiStore, ['isSidePanelOpen']),

    /**
     * The current view mode drives both context shape and AI section prompt selection.
     * "scope"     — workpackage scope editor is open
     * "scenarios" — workpackage is open and showing its scenario list
     * "none"      — no workpackage is open
     */
    viewMode() {
      if (!this.selectedWorkpackage?._id) return 'none';
      if (this.isWPSetupExpanded) return 'scope';
      if (this.isScenarioPanelExpanded) return 'scenarios';
      return 'none';
    },

    /** Scenarios belonging to the currently open workpackage. */
    workpackageScenarios() {
      if (!this.selectedWorkpackage?._id) return [];
      return this.scenarios.filter(s => s.workpackageId === this.selectedWorkpackage._id);
    },

    /**
     * The expanded scenario row: only populated when the scenario panel is open AND
     * selectedScenario has an _id. Never trust selectedScenario._id alone — it persists
     * across navigation and may be stale.
     */
    expandedScenario() {
      if (!this.isScenarioPanelExpanded || !this.selectedScenario?._id) return null;
      return this.selectedScenario;
    },
  },

  watch: {
    /**
     * Switch the AI section prompt and update the context breadcrumb whenever the
     * view mode changes (scope open/closed, workpackage opened/closed).
     */
    viewMode: {
      immediate: false,
      handler(newMode) {
        this.updateAiSidePanel(newMode);
      },
    },

    selectedWorkpackage(wp) {
      // Re-run in case viewMode didn't change but the WP name did (e.g. after rename).
      this.updateAiSidePanel(this.viewMode, wp);
    },
  },

  mounted() {
    const aiStore = useAiStore();
    aiStore.enableSidePanel({
      section: AI_SECTION_HOME,
      contextDisplay: this.buildContextDisplay(),
    });
    aiStore.setSidePanelContextFactory(() => this.buildInnerContext());
    aiStore.setContextReady(true);
  },

  methods: {
    ...mapActions(useWorkpackagesStore, [
      'copyWorkpackage',
      'toggleScenarioPanelExpanded',
      'toggleWorkpackageSetupExpanded',
      'updateWpDateSelections',
      'runWorkpackageSetupJob',
      'getWpDataForSave',
      'updateWorkpackage',
    ]),
    ...mapActions(useScenariosStore, [
      'createScenario',
      'updateScenario',
      'copyScenario',
      'deleteScenario',
    ]),

    /** Derive the breadcrumb labels shown in the AI panel header. */
    buildContextDisplay(mode = this.viewMode, wp = this.selectedWorkpackage) {
      if (!wp?._id) return ['Workpackages Overview'];
      if (mode === 'scope') return [wp.name, 'Scope Editor'].filter(Boolean);
      return [wp.name].filter(Boolean);
    },

    /**
     * Update the AI side panel section key and breadcrumb.
     * Called from watchers so the panel stays in sync without remounting.
     */
    updateAiSidePanel(mode = this.viewMode, wp = this.selectedWorkpackage) {
      const aiStore = useAiStore();
      const section = mode === 'scope' ? AI_SECTION_SCOPE : AI_SECTION_HOME;
      aiStore.enableSidePanel({ section, contextDisplay: this.buildContextDisplay(mode, wp) });
    },

    // ─────────────────────────────────────────────────────────────────────
    // Context builders
    // ─────────────────────────────────────────────────────────────────────

    /**
     * Build the inner context object sent to the AI assistant (called lazily at send-time).
     * All complex values are plain JS objects/arrays — the server serialises them to JSON.
     */
    buildInnerContext() {
      const mode = this.viewMode;

      return {
        viewMode: mode,
        workpackageSummary: this.buildWorkpackageSummary(),
        workpackage: mode !== 'none' ? this.buildWorkpackageContext() : null,
        scenarios: mode === 'scenarios' ? this.buildScenariosContext() : [],
        expandedScenario: mode === 'scenarios' ? this.buildExpandedScenarioContext() : null,
        scopeConfig: mode === 'scope' ? this.buildScopeConfig() : null,
      };
    },

    /**
     * A lightweight summary always present so the agent knows the workspace size
     * even when no workpackage is open. Caps recently-modified list to avoid bloat.
     */
    buildWorkpackageSummary() {
      const sorted = sortBy(this.activeWorkpackages, wp => {
        // Sort by lastModifiedDate descending if available
        const d = get(wp, 'lastModifiedDate') || get(wp, 'createdDate') || '';
        return d ? -new Date(d).getTime() : 0;
      });
      const recent = takeRight(sorted, 5).reverse();

      return {
        totalActive: this.activeWorkpackages.length,
        totalArchived: this.archivedWorkpackages?.length ?? 0,
        recentlyModified: recent.map(wp => ({
          name: wp.name,
          type: this.formatWpType(wp.type),
          lastModified: wp.lastModifiedDate || null,
        })),
      };
    },

    /** Core workpackage fields sent when a WP is open. */
    buildWorkpackageContext() {
      const wp = this.selectedWorkpackage;
      if (!wp) return null;
      return {
        _id: wp._id,
        name: wp.name,
        type: this.formatWpType(wp.type),
        description: wp.description || null,
        targetLaunchDate: wp.targetLaunchDate || null,
        businessUnit: wp.businessUnit || null,
        storeCount: wp.stores?.length ?? 0,
        productCount: wp.products?.length ?? 0,
        assortmentGroupNames: (wp.assortmentGroups || []).map(ag => ag.name || ag.key || ag),
        setupJobStatus: get(wp, 'jobs.workpackageSetup.status') || null,
      };
    },

    /** Scenario list for the open workpackage: one entry per scenario with per-section status. */
    buildScenariosContext() {
      return this.workpackageScenarios.map(scenario => ({
        _id: scenario._id,
        name: scenario.name,
        lastModified: scenario.lastModifiedDate || null,
        status: this.buildScenarioStatusContext(scenario),
      }));
    },

    /**
     * Per-section status for a scenario.
     * Uses the scenarioStatus computed (same logic as the scenario list UI) rather than
     * raw status paths, which are unreliable for sub-sections.
     */
    buildScenarioStatusContext(scenario) {
      const computed = this.scenarioStatus(scenario);
      const topLevelSections = [
        'space',
        'inputs',
        'measuring',
        'modelling',
        'assortment',
        'execution',
      ];
      const result = {};
      topLevelSections.forEach(section => {
        const s = computed[section] || {};
        result[section] = {
          status: s.status || sectionStatuses.notStarted,
          available: s.available ?? false,
        };
      });
      return result;
    },

    /**
     * Detail for the expanded scenario row.
     * Only includes fields present in the list-fetched object (not the fully-hydrated
     * loadScenario result) — no stores/cannGroups which would be null here.
     */
    buildExpandedScenarioContext() {
      const scenario = this.expandedScenario;
      if (!scenario) return null;
      const computed = this.scenarioStatus(scenario);
      const subSections = {
        space: ['furniture', 'mapping', 'storeclass', 'spacebreaks'],
        inputs: ['attributeEditor', 'cannGroups', 'varietyGroups'],
        measuring: ['switching', 'cdt', 'region', 'clustering'],
        modelling: ['productModelling'],
        assortment: [],
        execution: [],
      };

      const sections = {};
      Object.entries(subSections).forEach(([section, subs]) => {
        const s = computed[section] || {};
        sections[section] = {
          status: s.status || sectionStatuses.notStarted,
          available: s.available ?? false,
          subSections: subs.reduce((acc, sub) => {
            const subData = s[sub] || {};
            acc[sub] = {
              status: subData.status || sectionStatuses.notStarted,
              available: subData.available ?? false,
            };
            return acc;
          }, {}),
        };
      });

      return {
        _id: scenario._id,
        name: scenario.name,
        description: scenario.description || null,
        lastModified: scenario.lastModifiedDate || null,
        sections,
      };
    },

    /**
     * Scope configuration sent when viewMode === 'scope'.
     * Date fields are read from wpDateSelections (the live in-memory state that includes
     * defaults set by initWpSetupData) so the agent always sees the actual current values,
     * even before the user has saved for the first time.
     */
    buildScopeConfig() {
      const wp = this.selectedWorkpackage;
      if (!wp) return null;
      // wpDateSelections holds live (potentially unsaved) values; fall back to saved WP fields
      const dates = this.wpDateSelections || {};
      return {
        snapshotDate: dates.snapshotDate || wp.snapshotDate || null,
        performanceStartDate: dates.performanceStartDate || wp.performanceStartDate || null,
        performanceDateWeeks: dates.performanceDateWeeks ?? wp.performanceDateWeeks ?? null,
        performanceEndDate: dates.performanceEndDate || wp.performanceEndDate || null,
        switchingStartDate: dates.switchingStartDate || wp.switchingStartDate || null,
        switchingDateWeeks: dates.switchingDateWeeks ?? wp.switchingDateWeeks ?? null,
        switchingEndDate: dates.switchingEndDate || wp.switchingEndDate || null,
        targetLaunchDate: dates.targetLaunchDate || wp.targetLaunchDate || null,
        fillInSelection: wp.fillInSelection || null,
        furnitureDimensions: wp.furnitureDimensions || [],
        assortmentGroupNames: (wp.assortmentGroups || []).map(ag => ag.name || ag.key || ag),
        productCount: wp.products?.length ?? 0,
        storeCount: wp.stores?.length ?? 0,
        setupJobStatus: get(wp, 'jobs.workpackageSetup.status') || null,
      };
    },

    /** Normalise workpackage type to a readable label. */
    formatWpType(type) {
      if (!type) return 'Unknown';
      if (type === 'a_full_reset') return 'Full reset';
      return type;
    },
  },
};
</script>
